import React from 'react';
import './Skeleton.scss';

/**
 * Base skeleton block. Use for any placeholder shape.
 *
 * @param {string}  variant  'text' | 'circle' | 'rect' (default 'rect')
 * @param {string|number} width   CSS width  (e.g. '60%', 120)
 * @param {string|number} height  CSS height (e.g. 12, '1rem')
 */
export function Skeleton({ variant = 'rect', width, height, className = '', style = {} }) {
  const variantClass =
    variant === 'text' ? 'hrx-skeleton--text' : variant === 'circle' ? 'hrx-skeleton--circle' : '';

  return (
    <span
      className={`hrx-skeleton ${variantClass} ${className}`.trim()}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
        ...style
      }}
      aria-hidden="true"
    />
  );
}

/**
 * Table skeleton — drop-in replacement for a DataTable `progressComponent`.
 * Renders shimmer rows that visually stand in for table data while loading.
 *
 * @param {number} rows     number of placeholder rows (default 6)
 * @param {number} columns  number of placeholder columns (default 5)
 */
export function TableSkeleton({ rows = 6, columns = 5 }) {
  return (
    <div className="hrx-table-skeleton" role="status" aria-live="polite" aria-busy="true">
      <span className="visually-hidden">Loading…</span>
      {Array.from({ length: rows }).map((_, r) => (
        <div className="hrx-table-skeleton__row" key={r}>
          {Array.from({ length: columns }).map((_, c) => (
            <div className="hrx-table-skeleton__cell" key={c}>
              <Skeleton variant="text" width={c === 0 ? '55%' : `${60 + ((r + c) % 3) * 12}%`} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * ListSkeleton — vertical list of rows (icon/avatar + two text lines).
 * Good for file lists, notification-like panels, timelines.
 *
 * @param {number}  rows        number of rows (default 5)
 * @param {boolean} avatar      show a leading circle (default true)
 */
export function ListSkeleton({ rows = 5, avatar = true }) {
  return (
    <div className="hrx-list-skeleton" role="status" aria-live="polite" aria-busy="true">
      <span className="visually-hidden">Loading…</span>
      {Array.from({ length: rows }).map((_, r) => (
        <div className="hrx-list-skeleton__row" key={r}>
          {avatar ? <Skeleton variant="circle" width={38} height={38} /> : null}
          <div className="hrx-list-skeleton__lines">
            <Skeleton variant="text" width={`${50 + (r % 3) * 12}%`} />
            <Skeleton variant="text" width={`${30 + (r % 2) * 16}%`} height={10} />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * FormSkeleton — placeholder shaped like a form: rows of a short label line
 * above a full-width input bar. Good for modals/forms while data loads.
 *
 * @param {number}  rows     number of field rows (default 4)
 * @param {boolean} twoCol   render two fields per row (default false)
 */
export function FormSkeleton({ rows = 4, twoCol = false }) {
  return (
    <div className="hrx-form-skeleton" role="status" aria-live="polite" aria-busy="true">
      <span className="visually-hidden">Loading…</span>
      {Array.from({ length: rows }).map((_, r) => (
        <div
          className={`hrx-form-skeleton__row${twoCol ? ' hrx-form-skeleton__row--two' : ''}`}
          key={r}
        >
          <div className="hrx-form-skeleton__field">
            <Skeleton variant="text" width={`${28 + (r % 3) * 10}%`} height={10} />
            <Skeleton height={40} style={{ borderRadius: 10 }} />
          </div>
          {twoCol ? (
            <div className="hrx-form-skeleton__field">
              <Skeleton variant="text" width={`${24 + (r % 2) * 14}%`} height={10} />
              <Skeleton height={40} style={{ borderRadius: 10 }} />
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * CardSkeleton — a stat/summary card placeholder. Renders `count` cards
 * in a responsive grid; each shows a title line, big value, and a footer line.
 *
 * @param {number} count  number of cards (default 4)
 */
export function CardSkeleton({ count = 4 }) {
  return (
    <div className="hrx-card-skeleton" role="status" aria-live="polite" aria-busy="true">
      <span className="visually-hidden">Loading…</span>
      {Array.from({ length: count }).map((_, i) => (
        <div className="hrx-card-skeleton__card" key={i}>
          <Skeleton variant="text" width="55%" />
          <Skeleton variant="text" width="40%" height={22} />
          <Skeleton variant="text" width="70%" height={10} />
        </div>
      ))}
    </div>
  );
}

/**
 * PageSkeleton — a generic full-section placeholder: a header line, a row of
 * summary cards, then a block of list rows. Drop-in for full-page loaders.
 */
export function PageSkeleton({ cards = 4, rows = 6 }) {
  return (
    <div className="hrx-page-skeleton" role="status" aria-live="polite" aria-busy="true">
      <span className="visually-hidden">Loading…</span>
      <div className="hrx-page-skeleton__head">
        <Skeleton variant="text" width={220} height={20} />
        <Skeleton variant="text" width={120} height={16} />
      </div>
      <CardSkeleton count={cards} />
      <div className="hrx-page-skeleton__body">
        <ListSkeleton rows={rows} />
      </div>
    </div>
  );
}

export default Skeleton;
